import React from 'react'
import { Avatar, Button, Card, CardContent, CardHeader, Input, Label } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import {
  listAnalysisCourtesyEditors,
  updateAnalysisCourtesyLimit,
  type AnalysisCourtesyEditor,
} from '../services/analysisCourtesySettingsService'

export default function AnalysisCourtesySettings() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [editors, setEditors] = React.useState<AnalysisCourtesyEditor[]>([])
  const [draftLimits, setDraftLimits] = React.useState<Record<string, string>>({})
  const [mutatingId, setMutatingId] = React.useState<string | null>(null)

  const loadEditors = React.useCallback(async () => {
    setLoading(true)
    try {
      const rows = await listAnalysisCourtesyEditors()
      setEditors(rows)
      setDraftLimits((prev) => {
        const next = { ...prev }
        rows.forEach((row) => {
          if (next[row.id] === undefined) {
            next[row.id] = String(row.courtesyLimit)
          }
        })
        return next
      })
    } catch (e: any) {
      toast({
        title: 'Cortesia por editor (Analises)',
        description: e?.message || 'Falha ao carregar editores',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }, [toast])

  React.useEffect(() => {
    loadEditors()
  }, [loadEditors])

  function setDraftValue(userId: string, value: string) {
    setDraftLimits((prev) => ({ ...prev, [userId]: value }))
  }

  async function handleSave(userId: string) {
    if (mutatingId) return
    const raw = draftLimits[userId]
    const parsed = Number(raw)
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast({
        title: 'Cortesia por editor (Analises)',
        description: 'Informe um limite valido (0 ou maior).',
        variant: 'error',
      })
      return
    }

    const nextLimit = Math.max(0, Math.floor(parsed))
    setMutatingId(userId)
    try {
      await updateAnalysisCourtesyLimit(userId, nextLimit)
      toast({
        title: 'Cortesia por editor (Analises)',
        description: 'Limite atualizado com sucesso.',
        variant: 'success',
        soft: true,
      })
      await loadEditors()
    } catch (e: any) {
      toast({
        title: 'Cortesia por editor (Analises)',
        description: e?.message || 'Falha ao atualizar limite',
        variant: 'error',
      })
    } finally {
      setMutatingId(null)
    }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Cortesia por editor (postagens)</CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <div className="text-sm font-medium text-foreground/80">Limite mensal por usuario</div>
          <p className="text-xs text-foreground/60">
            Defina quantas cortesias cada editor pode conceder por mes. A cota reinicia automaticamente
            todo mes (master e ilimitado).
          </p>
        </div>

        {loading && (
          <div className="text-xs text-foreground/60">
            Carregando editores...
          </div>
        )}

        {!loading && editors.length === 0 && (
          <div className="text-xs text-foreground/60">
            Nenhum editor cadastrado ainda.
          </div>
        )}

        {!loading && editors.length > 0 && (
          <div className="space-y-2">
            {editors.map((editor) => {
              const draftValue = draftLimits[editor.id] ?? String(editor.courtesyLimit)
              const usageLabel = `${editor.courtesyUsed}/${editor.courtesyLimit}`
              const disabled = !!mutatingId

              return (
                <div
                  key={editor.id}
                  className="flex flex-col gap-3 rounded-md border border-border/60 px-3 py-2 md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar
                      src={editor.avatarUrl || undefined}
                      alt={editor.fullName || 'Usuario'}
                      className="h-7 w-7"
                      fallback={(editor.fullName?.[0] || 'U')}
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">
                        {editor.fullName || '(Sem nome)'}
                      </div>
                      <div className="truncate text-xs text-foreground/60">
                        {editor.email || '—'}
                        {editor.role ? ` • ${editor.role}` : ''}
                      </div>
                      <div className="text-[11px] text-foreground/60">
                        Cortesias usadas no mes: {usageLabel}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-foreground/60">Limite</Label>
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        value={draftValue}
                        onChange={(e) => setDraftValue(editor.id, e.target.value)}
                        className="h-9 w-[120px]"
                        disabled={disabled}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={disabled}
                      onClick={() => handleSave(editor.id)}
                    >
                      Salvar
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
